'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/chat-interface/ui/button';
import { RiVoiceAiLine, RiChatAiLine } from '@remixicon/react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { X, ArrowLeft, Trash2 } from 'lucide-react';
import { getHeaderButtonClasses, getHeaderTitleClasses, WidgetStyle } from '@/lib/chat-interface/widget-styles';
import { logger } from '@/lib/logger';

interface ChatHeaderProps {
  chatId: string;
  selectedModelId: string;
  selectedVisibilityType: any;
  isReadonly: boolean;
  chatTitle?: string | null;
  currentMode: 'text' | 'voice';
  onModeChange: (mode: 'text' | 'voice') => void;
  isThinking?: boolean;
  isWelcomeMode?: boolean;
  isMobileMode?: boolean;
  onMobileClose?: () => void;
  widgetStyle?: WidgetStyle;
  chatVoiceEnabled?: boolean;
  iconType?: 'orb' | 'logo';
  agentAvatarURL?: string | null;
  agentConfig?: {
    riveAnimationType?: string;
    riveOrbCustomColor?: string;
    riveOrbColor?: number;
  };
  theme?: 'light' | 'dark';
  onNewThread?: () => void;
}

function ChatHeaderComponent({
  chatId,
  selectedModelId,
  selectedVisibilityType,
  isReadonly,
  chatTitle,
  currentMode,
  onModeChange,
  isThinking = false,
  isWelcomeMode = false,
  isMobileMode = false,
  onMobileClose,
  widgetStyle = 'glass',
  chatVoiceEnabled = false,
  iconType,
  agentAvatarURL,
  agentConfig,
  theme = 'light',
  onNewThread,
}: ChatHeaderProps) {
  const router = useRouter();

  const handleNewChat = () => {
    if (onNewThread) {
      onNewThread();
    } else {
      // Fallback to reload if no callback provided
      window.location.reload();
    }
  };

  const handleModeToggle = () => {
    onModeChange(currentMode === 'text' ? 'voice' : 'text');
  };

  // Theme-aware header controls
  const buttonClasses = getHeaderButtonClasses(widgetStyle, theme);
  const titleClasses = getHeaderTitleClasses(widgetStyle, theme);

  const getTooltipStyles = () => {
    switch (widgetStyle) {
      case 'black':
        return '!bg-neutral-800 !text-white !border !border-neutral-600 !rounded-lg [&_svg]:!bg-neutral-800 [&_svg]:!fill-neutral-800 [&>svg]:!bg-neutral-800 [&>svg]:!fill-neutral-800';
      case 'glass':
        return theme === 'dark'
          ? '!bg-black/80 !backdrop-blur-sm !text-white !border !border-white/20 !rounded-lg [&_svg]:!bg-black/80 [&_svg]:!fill-black/80 [&>svg]:!bg-black/80 [&>svg]:!fill-black/80'
          : '!bg-white !text-black !border !border-neutral-200 !rounded-lg [&_svg]:!bg-white [&_svg]:!fill-white [&>svg]:!bg-white [&>svg]:!fill-white';
      case 'white':
      default:
        return theme === 'dark'
          ? '!bg-neutral-800 !text-white !border !border-neutral-600 !rounded-lg [&_svg]:!bg-neutral-800 [&_svg]:!fill-neutral-800 [&>svg]:!bg-neutral-800 [&>svg]:!fill-neutral-800'
          : '!bg-white !text-black !border !border-neutral-200 !rounded-lg [&_svg]:!bg-white [&_svg]:!fill-white [&>svg]:!bg-white [&>svg]:!fill-white';
    }
  };

  const getHeaderBackground = () => {
    switch (widgetStyle) {
      case 'white':
        return theme === 'dark' ? 'bg-neutral-900' : 'bg-white';
      case 'black':
        return 'bg-black';
      case 'glass':
      default:
        return theme === 'dark' ? 'bg-transparent' : 'bg-white';
    }
  };

  return (
    <TooltipProvider>
      <header className={`flex sticky top-0 py-1.5 items-center px-4 h-12 z-10 relative ${getHeaderBackground()}`}>
        <div className="flex w-full items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {chatVoiceEnabled && currentMode === 'voice' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  onClick={() => onModeChange('text')}
                  variant="ghost"
                  size="icon" 
                  className={`size-7 rounded-sm ${buttonClasses}`}
                >
                  <ArrowLeft className="size-4" />
                  <span className="sr-only">Return to Text Mode</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent className={getTooltipStyles()}>
                <p>Return to Text Mode</p>
              </TooltipContent>
            </Tooltip>
          )}
          {chatVoiceEnabled && currentMode === 'text' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  onClick={handleModeToggle}
                  variant="ghost"
                  size="icon" 
                  className={`size-7 rounded-sm ${buttonClasses}`}
                >
                  <RiVoiceAiLine className="size-4" />
                  <span className="sr-only">Switch to Voice Mode</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent className={getTooltipStyles()}>
                <p>Switch to Voice Mode</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        
        <div className="flex-1 flex items-center justify-center gap-2 overflow-hidden">
          {chatTitle && (
            <span className={`text-sm font-semibold ${titleClasses} overflow-hidden whitespace-nowrap text-ellipsis`}>
              {chatTitle}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                onClick={handleNewChat}
                variant="ghost" 
                size="icon" 
                className={`size-7 rounded-sm ${buttonClasses}`}
              >
                <Trash2 className="size-3.5" />
                <span className="sr-only">New Chat</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className={getTooltipStyles()}>
              <p>New Chat</p>
            </TooltipContent>
          </Tooltip>
          
          {isMobileMode && onMobileClose && (
            <Button 
              type="button"
              onClick={(e) => {
                logger.debug('X button clicked in iframe!', { hasOnMobileClose: !!onMobileClose }, 'chat-header');
                e.preventDefault();
                e.stopPropagation();
                if (onMobileClose) {
                  onMobileClose();
                } else {
                  logger.error('onMobileClose is not defined!', undefined, 'chat-header');
                }
              }}
              onTouchStart={(e) => {
                // Ensure touch events work on mobile
                e.stopPropagation();
              }}
              variant="ghost" 
              size="icon" 
              className={`size-7 rounded-sm ${buttonClasses} relative z-10`}
              style={{ pointerEvents: 'auto', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              data-testid="chat-header-close-button"
              title="ChatHeader Close Button (Inside Iframe)"
            >
              <X className="size-3.5" />
              <span className="sr-only">Close Chat</span>
            </Button>
          )}
        </div>
      </div>
      
      {/* Animated gradient border - idle mode (welcome) or thinking mode */}
      {(isWelcomeMode || isThinking) && (
        <div className="absolute bottom-0 left-0 right-0 h-[1px] overflow-hidden">
          <div className={`absolute inset-0 transition-opacity duration-1000 ease-out ${
            isThinking ? 'animate-gradient-thinking opacity-60' : 'animate-gradient-idle opacity-60'
          }`}></div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes gradient-flow-idle {
          0% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        @keyframes gradient-flow-thinking {
          0% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        .animate-gradient-idle {
          animation: gradient-flow-idle 8s ease-in-out infinite;
          background: linear-gradient(
            90deg,
            transparent 0%,
            transparent 5%,
            #f97316 20%,
            #3b82f6 50%,
            #8b5cf6 80%,
            transparent 95%,
            transparent 100%
          );
          background-size: 200% 100%;
          box-shadow: 0 0 3px rgba(249, 115, 22, 0.3), 0 0 6px rgba(59, 130, 246, 0.2), 0 0 9px rgba(139, 92, 246, 0.2);
        }
        .animate-gradient-thinking {
          animation: gradient-flow-thinking 2s ease-in-out infinite;
          background: linear-gradient(
            90deg,
            transparent 0%,
            transparent 5%,
            #f97316 20%,
            #3b82f6 50%,
            #8b5cf6 80%,
            transparent 95%,
            transparent 100%
          );
          background-size: 200% 100%;
          box-shadow: 0 0 3px rgba(249, 115, 22, 0.4), 0 0 6px rgba(59, 130, 246, 0.3), 0 0 9px rgba(139, 92, 246, 0.3);
        }
        .dark .animate-gradient-idle {
          background: linear-gradient(
            90deg,
            transparent 0%,
            transparent 5%,
            #fb923c 20%,
            #60a5fa 50%,
            #a78bfa 80%,
            transparent 95%,
            transparent 100%
          );
          background-size: 200% 100%;
          box-shadow: 0 0 3px rgba(251, 146, 60, 0.3), 0 0 6px rgba(96, 165, 250, 0.2), 0 0 9px rgba(167, 139, 250, 0.2);
        }
        .dark .animate-gradient-thinking {
          background: linear-gradient(
            90deg,
            transparent 0%,
            transparent 5%,
            #fb923c 20%,
            #60a5fa 50%,
            #a78bfa 80%,
            transparent 95%,
            transparent 100%
          );
          background-size: 200% 100%;
          box-shadow: 0 0 3px rgba(251, 146, 60, 0.4), 0 0 6px rgba(96, 165, 250, 0.3), 0 0 9px rgba(167, 139, 250, 0.3);
        }
      `}</style>
      </header>
    </TooltipProvider>
  );
}

// Memoize ChatHeader to prevent unnecessary re-renders
export const ChatHeader = React.memo(ChatHeaderComponent, (prevProps, nextProps) => {
  // Custom comparison: only re-render if these props change
  return (
    prevProps.chatId === nextProps.chatId &&
    prevProps.chatTitle === nextProps.chatTitle &&
    prevProps.currentMode === nextProps.currentMode &&
    prevProps.isThinking === nextProps.isThinking &&
    prevProps.isWelcomeMode === nextProps.isWelcomeMode &&
    prevProps.isMobileMode === nextProps.isMobileMode &&
    prevProps.widgetStyle === nextProps.widgetStyle &&
    prevProps.chatVoiceEnabled === nextProps.chatVoiceEnabled &&
    prevProps.iconType === nextProps.iconType &&
    prevProps.agentAvatarURL === nextProps.agentAvatarURL &&
    prevProps.theme === nextProps.theme &&
    JSON.stringify(prevProps.agentConfig) === JSON.stringify(nextProps.agentConfig)
  );
});
